import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

const PHOTO_URL = "https://customer-assets.emergentagent.com/job_06c22da0-53e2-45fc-a666-faa4c956a6c6/artifacts/azlm5cig_IMG_6115.JPG.jpeg";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Entrar — Ana Paula Moraes";
  }, []);

  useEffect(() => {
    if (user) {
      const target = user.role === "admin" ? "/admin" : "/membros";
      navigate(target, { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Bem-vinda de volta, ${u.name.split(" ")[0]}!`);
      const dest = location.state?.from?.pathname || (u.role === "admin" ? "/admin" : "/membros");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      {/* === Mobile/Tablet hero banner === */}
      <div className="lg:hidden relative h-48 sm:h-64 overflow-hidden">
        <img src={PHOTO_URL} alt="" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(196,143,149,0.55), rgba(184,115,51,0.55))" }} />
        <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-7 text-white">
          <Link to="/" className="brand-logo text-lg" style={{ color: "white" }} data-testid="login-mobile-logo">
            <span style={{ color: "white" }}>Ana Paula</span>{" "}
            <span style={{ color: "#FBE9D5", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>Moraes</span>
          </Link>
          <div>
            <div className="font-display italic text-2xl sm:text-3xl leading-tight">Eu sou a CEO da minha vida.</div>
            <div className="text-[10px] uppercase tracking-[0.32em] opacity-90 mt-2">Área de Membros</div>
          </div>
        </div>
      </div>

      {/* === Desktop image side === */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={PHOTO_URL} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(196,143,149,0.4), rgba(184,115,51,0.5))" }} />
        <div className="absolute top-12 left-12">
          <Link to="/" className="brand-logo text-2xl" style={{ color: "white" }}>
            <span style={{ color: "white" }}>Ana Paula</span>{" "}
            <span style={{ color: "#FBE9D5", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>Moraes</span>
          </Link>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.32em] opacity-80 mb-4">O Convite</div>
          <div className="font-serif text-5xl xl:text-6xl italic leading-[1.05] mb-4 tracking-tight">
            Eu sou a CEO<br />da minha vida.
          </div>
          <div className="flex items-center gap-3 mt-6">
            <div className="w-12 h-px bg-white/60" />
            <span className="text-xs uppercase tracking-[0.28em] opacity-90">Marca pessoal · Liderança feminina</span>
          </div>
        </div>
      </div>

      {/* === Form side === */}
      <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-10 sm:py-14 lg:py-20">
        <div className="max-w-md w-full mx-auto fade-in-up">
          {/* Mobile: no logo here (in hero), tablet+: small logo */}
          <Link to="/" className="brand-logo text-2xl mb-8 sm:mb-10 lg:mb-12 hidden sm:inline-block lg:inline-block">
            <span className="ana">Ana Paula</span> <span className="m">Moraes</span>
          </Link>

          <div className="flex items-center gap-2 mb-3">
            <span className="dot-ornament" />
            <span className="eyebrow">Acesso</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-3 leading-[1.05] tracking-tight" style={{ color: "var(--text-primary)" }}>
            Bem-vinda <span className="headline-italic-accent">de volta</span>
          </h1>
          <p className="text-[15px] mb-8 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Entre com seus dados para acessar seus conteúdos.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>E-mail</label>
              <input
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                className="brand-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Senha</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  className="brand-input pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-[color:var(--bg-warm)] transition-colors"
                  aria-label="Mostrar senha"
                  data-testid="login-toggle-password"
                  style={{ color: "var(--text-muted)" }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="text-sm py-3 px-4 rounded-xl" style={{ background: "rgba(220,38,38,0.06)", color: "#b91c1c", border: "1px solid rgba(220,38,38,0.15)" }} data-testid="login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting} className="btn-primary w-full mt-2" data-testid="login-submit-btn">
              {submitting ? "Entrando…" : (<>Entrar <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-10 pt-6" style={{ borderTop: "1px solid var(--border-light)" }}>
            <div className="text-center text-sm" style={{ color: "var(--text-secondary)" }}>
              Ainda não tem uma conta?{" "}
              <Link to="/cadastro" className="font-medium hover:underline" style={{ color: "var(--copper)" }} data-testid="login-to-register">
                Cadastre-se
              </Link>
            </div>
            <div className="mt-3 text-center text-xs" style={{ color: "var(--text-muted)" }}>
              <Link to="/admin/login" className="hover:underline" data-testid="login-to-admin">Acesso administrativo</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
